import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SquareArrowOutUpRight, Lock } from 'lucide-react';

const FREQUENTLY_USED_TAGS = [
  { id: 'gaza-war', label: 'Gaza War', color: '#fecaca' },
  { id: 'october-7', label: 'October 7', color: '#fed7aa' },
  { id: 'surgery', label: 'Surgery', color: '#bfdbfe' },
  { id: 'sydney-massacre', label: 'Sydney Massacre', color: '#ddd6fe' }
];

export default function TellTheStoryPage() {
  const navigate = useNavigate();
  const [dedication, setDedication] = useState(null);
  const [chainType, setChainType] = useState(null);
  const [whoData, setWhoData] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [story, setStory] = useState('');
  const [isPublic, setIsPublic] = useState(false); // false = public (toggle OFF), true = private (toggle ON)
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Device detection for app store redirect
  const handleAppStoreRedirect = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let appStoreUrl;
    
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    } else if (/android/i.test(userAgent)) {
      appStoreUrl = 'https://play.google.com/store/apps/details?id=us.yesod.app';
    } else {
      appStoreUrl = 'https://apps.apple.com/us/app/yesod/id6748472269';
    }
    
    window.open(appStoreUrl, '_blank');
  };

  useEffect(() => {
    const savedDedication = sessionStorage.getItem('chainDedication');
    const savedChainType = sessionStorage.getItem('chainType');
    const savedWhoData = sessionStorage.getItem('whoIsThisForData');
    
    if (!savedDedication) {
      navigate('/create-chain');
      return;
    }
    
    if (!savedChainType) {
      navigate('/create-chain/type');
      return;
    }

    if (!savedWhoData) {
      navigate('/create-chain/details');
      return;
    }
    
    setDedication(savedDedication);
    setChainType(savedChainType);
    setWhoData(JSON.parse(savedWhoData));

    // Don't load existing data - always start fresh
    // User should enter new data each time they create a chain
  }, [navigate]);

  const handleToggleFrequentTag = (tagLabel) => {
    // If clicking the same tag, clear it; otherwise set it
    if (selectedTag === tagLabel) {
      setSelectedTag('');
      setInputValue('');
    } else {
      setSelectedTag(tagLabel);
      setInputValue('');
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      setSelectedTag(inputValue.trim());
      setInputValue('');
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      setSelectedTag(inputValue.trim());
      setInputValue('');
    }
  };

  const handleRemoveTag = () => {
    setSelectedTag('');
    setInputValue('');
  };

  const handleBack = () => {
    // Save current data
    sessionStorage.setItem('storyData', JSON.stringify({
      tag: selectedTag,
      story,
      isPublic
    }));
    navigate('/create-chain/details');
  };

  const handleClose = () => {
    sessionStorage.removeItem('chainDedication');
    sessionStorage.removeItem('chainType');
    sessionStorage.removeItem('whoIsThisForData');
    sessionStorage.removeItem('storyData');
    navigate('/');
  };

  const generateSessionId = () => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleReview = async () => {
    // Save all data to sessionStorage
    sessionStorage.setItem('chainDedication', dedication);
    sessionStorage.setItem('chainType', chainType);
    sessionStorage.setItem('whoIsThisForData', JSON.stringify(whoData));
    sessionStorage.setItem('storyData', JSON.stringify({
      tag: selectedTag,
      story,
      isPublic
    }));

    // Navigate directly to auth
    navigate('/auth');
  };

  if (!dedication || !chainType || !whoData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <img 
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png" 
            alt="Yesod" 
            className="h-10"
          />
          <h1 className="text-xl font-semibold text-black">Yesod</h1>
        </div>
        <button 
          onClick={handleAppStoreRedirect}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#667eea] rounded-lg hover:bg-[#5568d3] transition-all duration-200 cursor-pointer"
        >
          Continue in App
          <SquareArrowOutUpRight size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 px-5 pb-32">
        <div className="max-w-2xl mx-auto pt-8">
          <h2 className="text-3xl font-bold text-[#667eea] mb-2">Tell the Story</h2>
          <p className="text-base text-gray-600 mb-8">Context creates connection. Chains with a personal story get 2x more participants.</p>

          {/* Tags Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Add a Tag</h3>
            <p className="text-sm text-gray-500 mb-4">Help others find your chain</p>

            <div className="relative flex items-center gap-2 w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus-within:border-[#667eea] focus-within:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] transition-all">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" strokeWidth="2"/>
                <line x1="7" y1="7" x2="7.01" y2="7" strokeWidth="2"/>
              </svg>
              
              {selectedTag && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-900 text-sm font-medium rounded-full shrink-0">
                  <span>{selectedTag}</span>
                  <button 
                    onClick={handleRemoveTag}
                    className="text-gray-600 hover:text-gray-900 text-lg leading-none"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {!selectedTag && (
                <input
                  type="text"
                  className="flex-1 min-w-0 text-base outline-none bg-transparent"
                  placeholder="e.g. Surgery, Refuah, Israel..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleInputKeyPress}
                  onBlur={handleInputBlur}
                />
              )}
            </div>

            {/* Frequently Used */}
            <div className="mt-6">
              <p className="text-xs font-semibold text-gray-500 mb-3 tracking-wider">FREQUENTLY USED:</p>
              <div className="flex flex-wrap gap-3">
                {FREQUENTLY_USED_TAGS.map((tag) => (
                  <button
                    key={tag.id}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                      selectedTag === tag.label
                        ? 'ring-2 ring-[#667eea] ring-offset-2 shadow-md' 
                        : 'hover:shadow-md'
                    }`}
                    style={{ backgroundColor: tag.color }}
                    onClick={() => handleToggleFrequentTag(tag.label)}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Story Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">The Story</h3>
            <p className="text-sm text-gray-500 mb-4">Share the story behind this {chainType === 'tehillim' ? 'Tehillim' : 'Challah'} chain</p>
            
            <div className="relative">
              <svg className="absolute left-4 top-4 text-gray-400" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" strokeWidth="2"/>
                <polyline points="14 2 14 8 20 8" strokeWidth="2"/>
                <line x1="16" y1="13" x2="8" y2="13" strokeWidth="2"/>
                <line x1="16" y1="17" x2="8" y2="17" strokeWidth="2"/>
                <polyline points="10 9 9 9 8 9" strokeWidth="2"/>
              </svg>
              <textarea
                className="w-full pl-12 pr-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#667eea] focus:shadow-[0_0_0_3px_rgba(102,126,234,0.1)] transition-all resize-none"
                rows="5"
                placeholder="e.g., 'Moshe is going in for surgery this Tuesday at 9 AM. Please have him in mind.' or 'Praying for the safety of our soldiers.' Keep it brief but personal."
                value={story}
                onChange={(e) => setStory(e.target.value)}
              ></textarea>
            </div>
          </div>

          {/* Public/Private Chain Toggle - Moved closer to story */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl mb-6">
            <div className="flex items-center gap-4">
              {isPublic ? (
                <Lock className="text-gray-600" size={24} />
              ) : (
                <svg className="text-gray-600" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                  <line x1="2" y1="12" x2="22" y2="12" strokeWidth="2"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" strokeWidth="2"/>
                </svg>
              )}
              <div>
                <h4 className="text-base font-semibold text-gray-900">{isPublic ? 'Private Chain' : 'Public Chain'}</h4>
                <p className="text-sm text-gray-600">{isPublic ? 'Anyone with the link can access it' : 'Anyone can access it'}</p>
              </div>
            </div>
            <label className="relative inline-block w-14 h-8 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="peer sr-only"
              />
              <span className="absolute inset-0 bg-gray-300 rounded-full transition-colors duration-200 peer-checked:bg-[#667eea]"></span>
              <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 peer-checked:translate-x-6 shadow-md"></span>
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border-2 border-red-200 rounded-xl text-red-800 mb-6">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <line x1="12" y1="8" x2="12" y2="12" strokeWidth="2"/>
                <line x1="12" y1="16" x2="12.01" y2="16" strokeWidth="2"/>
              </svg>
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-5 py-4 mt-auto">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-[#667eea]"></div>
            <div className="w-2 h-2 rounded-full bg-[#667eea]"></div>
            <div className="w-2 h-2 rounded-full bg-[#667eea]"></div>
            <div className="w-8 h-2 rounded bg-[#667eea]"></div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <button 
              type="button" 
              className="px-6 py-3 text-base font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed" 
              onClick={handleBack} 
              disabled={isLoading}
            >
              <span className="border-b-2 border-gray-300">Back</span>
            </button>
            <button
              type="button"
              className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-br from-[#667eea] to-[#764ba2] rounded-lg hover:translate-y-[-2px] hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              onClick={handleReview}
              disabled={isLoading}
            >
              Continue
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4 shadow-2xl">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-[#667eea] rounded-full animate-spin"></div>
            <p className="text-base font-medium text-gray-900">Saving...</p>
          </div>
        </div>
      )}
    </div>
  );
}
